'use client';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteLoaderData } from 'react-router';
import { getPriceData } from '@/components/product-price/utils';
import { formatCurrency } from '@/lib/currency';
import { getOmnibusPriceIndication, type OmnibusPriceDisplay } from '@/lib/omnibus';
import type { ProductSearchHitLike } from '@/lib/product-utils-plp';
import type { loader as rootLoader } from '@/root';
import { computeSaleDiscountPercent, formatPricePerUmLine } from './compute-product-price-weight';
import { getProductTileUnitRow } from 'src/lib/product-attributes-pim';

export type ProductForPriceWeightDisplay = Parameters<typeof getPriceData>[0];

export type UseProductPriceWeightDisplayOptions = {
    /**
     * Product used for headline price / promo (e.g. multipack bundle).
     * Defaults to `product` (the weight-attribute source).
     */
    priceProduct?: ProductForPriceWeightDisplay;
    /** Multipack pack count for €/um (defaults to 1). */
    packQty?: number;
    /**
     * When false, keep the catalog/transaction comparison and omit the omnibus tooltip.
     * Line cards (cart, checkout, orders) leave this on.
     */
    applyOmnibus?: boolean;
};

/**
 * Unit-sale display values shared by PLP tile and PDP (per-unit price, no quantity multiplier).
 *
 * When the SCAPI hooks expose an Omnibus previous price (`c_priorBestPrice`, EATWS-472) that is
 * higher than the selling price, it becomes the compare-at price: `listPrice` / `listFormatted`
 * and the `pricePct` badge are computed against it instead of the catalog list price, and
 * `omnibus` carries the struck-through previous total / per-unit prices and the promotion period.
 * A known historical price at or below the selling price suppresses catalog-based reductions.
 */
export function useProductPriceWeightDisplay(
    product: ProductForPriceWeightDisplay,
    currencyCode: string,
    options?: UseProductPriceWeightDisplayOptions
) {
    const { i18n } = useTranslation('product');
    const priceProduct = options?.priceProduct ?? product;
    const packQty = options?.packQty;
    const applyOmnibus = options?.applyOmnibus ?? true;
    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const pieceUnitOfMeasure = rootData?.erpPieceUnitOfMeasure;

    return useMemo(() => {
        const priceData = getPriceData(priceProduct);
        const { currentPrice } = priceData;
        const locale = i18n.language;
        const code = currencyCode || 'EUR';
        const hitLike = product as ProductSearchHitLike;

        const indication = applyOmnibus ? getOmnibusPriceIndication(priceProduct) : null;
        const priorBestPrice = indication?.priorBestPrice ?? null;
        const isReduction = priorBestPrice !== null && priorBestPrice > currentPrice;
        const isOnSale = priorBestPrice !== null ? isReduction : priceData.isOnSale;
        const listPrice = priorBestPrice ?? priceData.listPrice;
        const pricePct = computeSaleDiscountPercent(isOnSale, listPrice, currentPrice);
        const unitRow = getProductTileUnitRow(hitLike, {
            effectiveUnitPrice: currentPrice,
            packQty,
            pieceUnitOfMeasure,
        });
        const perUnitLine = formatPricePerUmLine(unitRow, locale, code);
        const currentFormatted = formatCurrency(currentPrice, locale, code);
        const listFormatted =
            isOnSale && listPrice != null && listPrice > 0 ? formatCurrency(listPrice, locale, code) : null;

        const hasOmnibusContent = indication && (priorBestPrice !== null || indication.validFrom || indication.validTo);
        const omnibus: OmnibusPriceDisplay | null = hasOmnibusContent
            ? {
                  ...indication,
                  priorBestPrice,
                  priorFormatted: isReduction ? formatCurrency(priorBestPrice, locale, code) : null,
                  priorPerUnitLine: isReduction
                      ? formatPricePerUmLine(
                            getProductTileUnitRow(hitLike, {
                                effectiveUnitPrice: priorBestPrice,
                                packQty,
                                pieceUnitOfMeasure,
                            }),
                            locale,
                            code
                        )
                      : null,
              }
            : null;

        return {
            unitRow,
            perUnitLine,
            pricePct,
            currentFormatted,
            listFormatted,
            isOnSale,
            currentPrice,
            listPrice,
            omnibus,
        };
    }, [currencyCode, i18n.language, product, priceProduct, packQty, pieceUnitOfMeasure, applyOmnibus]);
}
